/**
 * Card classifier for the Monte Carlo simulation engine.
 *
 * Converts DeckEntry[] into SimSpellCard[], SimCommander[], and draw schedules.
 * All classification is oracle-text pattern matching — no network calls.
 * Architecture §5, research-spec §5.
 */
import { isLand } from '../utils/card-helpers.js';
// ─── Mana cost parsing ─────────────────────────────────────────────────────────
/**
 * Parse a Scryfall mana cost string like "{2}{W}{B}" into color requirements and CMC.
 *
 * Rules (architecture §5.3):
 * - Pure colored pip (W|U|B|R|G): add 1 to that color
 * - Hybrid pip (W/U): store as composite key "W/U" with count 1
 * - Phyrexian single (W/P): treat as W, add 1
 * - X: single X → add 3 to generic; XX → add 2; XXX → add 1 (CMC modeling)
 * - Generic N: add N to generic (for CMC calculation, not in colorRequirements)
 */
export function parseManaRequirements(manaCost) {
    const requirements = new Map();
    let cmc = 0;
    if (!manaCost)
        return { requirements, cmc };
    // Tokenize: extract all {token} parts
    const tokens = manaCost.match(/\{([^}]+)\}/g) ?? [];
    // Count X occurrences
    let xCount = 0;
    for (const token of tokens) {
        const inner = token.slice(1, -1); // strip { }
        if (inner === 'X') {
            xCount++;
            continue; // handled after counting
        }
        // Pure colored pip: single letter W, U, B, R, G
        if (/^[WUBRG]$/.test(inner)) {
            requirements.set(inner, (requirements.get(inner) ?? 0) + 1);
            cmc += 1;
            continue;
        }
        // Hybrid pip: W/U, W/B, etc. (also covers Phyrexian W/P)
        if (inner.includes('/')) {
            const parts = inner.split('/');
            const a = parts[0] ?? '';
            const b = parts[1] ?? '';
            if (b === 'P') {
                // Phyrexian: treat as regular pip of color a
                if (/^[WUBRG]$/.test(a)) {
                    requirements.set(a, (requirements.get(a) ?? 0) + 1);
                    cmc += 1;
                }
            }
            else if (/^[WUBRG]$/.test(a) && /^[WUBRG]$/.test(b)) {
                const key = `${a}/${b}`;
                requirements.set(key, (requirements.get(key) ?? 0) + 1);
                cmc += 1;
            }
            else if (/^[WUBRG]$/.test(a) && /^\d+$/.test(b)) {
                // e.g., {2/W} — treat as colored pip
                requirements.set(a, (requirements.get(a) ?? 0) + 1);
                cmc += 1;
            }
            continue;
        }
        // Generic mana: numeric
        if (/^\d+$/.test(inner)) {
            cmc += parseInt(inner, 10);
            continue;
        }
        // Snow, colorless {C}, tap {T}, etc. — ignore for color requirements
    }
    // X handling: single X → CMC +3, double XX → +2, triple XXX → +1
    if (xCount === 1)
        cmc += 3;
    else if (xCount === 2)
        cmc += 2;
    else if (xCount >= 3)
        cmc += 1;
    return { requirements, cmc };
}
// ─── Spell weight ──────────────────────────────────────────────────────────────
function computeSpellWeight(cmc, hasColoredPips) {
    if (!hasColoredPips)
        return 0.0;
    if (cmc <= 2)
        return 1.5;
    if (cmc <= 4)
        return 1.0;
    return 0.75;
}
// ─── Color parsing from Add clauses ──────────────────────────────────────────
/**
 * Extract produced colors from oracle text Add clause.
 * Handles patterns like "Add {W}", "Add {W} or {U}", "Add one mana of any color", etc.
 */
function parseProducedColors(oracleText) {
    const colors = new Set();
    const colorMap = {
        W: 'W',
        U: 'U',
        B: 'B',
        R: 'R',
        G: 'G',
    };
    if (/any color/i.test(oracleText) || /any combination/i.test(oracleText)) {
        colors.add('W');
        colors.add('U');
        colors.add('B');
        colors.add('R');
        colors.add('G');
        return colors;
    }
    const addMatches = oracleText.matchAll(/Add\s+([^.]+)/gi);
    for (const match of addMatches) {
        const clause = match[1];
        if (!clause)
            continue;
        const pipMatches = clause.matchAll(/\{([WUBRG])\}/g);
        for (const pip of pipMatches) {
            const c = pip[1];
            if (c && colorMap[c])
                colors.add(colorMap[c]);
        }
    }
    return colors;
}
// ─── Mana producer classification ─────────────────────────────────────────────
function classifyManaProducer(name, cmc, typeLine, oracleText, requirements, hasColoredPips) {
    const typeLineLower = typeLine.toLowerCase();
    const oracleLower = oracleText.toLowerCase();
    let producerType = null;
    let survivalRate = 1.0;
    let searchedLandEntersTapped;
    let producedColors = new Set();
    if (typeLineLower.includes('creature') && /\{t\}: add/i.test(oracleText)) {
        producerType = 'dork';
        survivalRate = 0.5;
        producedColors = parseProducedColors(oracleText);
    }
    else if (typeLineLower.includes('artifact') && /\{t\}: add/i.test(oracleText)) {
        producerType = 'rock';
        survivalRate = 0.75;
        producedColors = parseProducedColors(oracleText);
    }
    else if (typeLineLower.includes('enchantment') && /add/i.test(oracleText) && !/\{t\}: add/i.test(oracleText)) {
        producerType = 'enchantment';
        survivalRate = 0.85;
        producedColors = parseProducedColors(oracleText);
    }
    else if (/search your library for a.{0,20}land/i.test(oracleText) && !typeLineLower.includes('land')) {
        producerType = 'landSearch';
        survivalRate = 1.0;
        searchedLandEntersTapped = /tapped/i.test(oracleText);
        producedColors = new Set(['W', 'U', 'B', 'R', 'G']); // land-search finds any color
    }
    if (!producerType)
        return null;
    return {
        name,
        cmc,
        colorRequirements: requirements,
        weight: computeSpellWeight(cmc, hasColoredPips),
        isLand: false,
        isManaProducer: true,
        producedColors,
        producerType,
        survivalRate,
        ...(searchedLandEntersTapped !== undefined ? { searchedLandEntersTapped } : {}),
    };
}
// ─── Card draw classification ──────────────────────────────────────────────────
function classifyCardDraw(name, cmc, oracleText, requirements, hasColoredPips) {
    // Repeated draw: triggers each turn
    if (/at the beginning of (your|each) upkeep|whenever .+ (you )?draw|each (turn|upkeep).*(draw|draws)/i.test(oracleText)) {
        return {
            name,
            cmc,
            colorRequirements: requirements,
            weight: computeSpellWeight(cmc, hasColoredPips),
            isLand: false,
            isCardDraw: true,
            cardsDrawn: 1, // one-shot draw count (irrelevant for repeated)
            isRepeatedDraw: true,
            cardsPerTurn: 1, // conservative default
        };
    }
    // One-shot draw
    const drawMatch = oracleText.match(/draw (a card|(\d+) cards?)/i);
    if (drawMatch) {
        const cardsDrawn = drawMatch[2] ? parseInt(drawMatch[2], 10) : 1;
        return {
            name,
            cmc,
            colorRequirements: requirements,
            weight: computeSpellWeight(cmc, hasColoredPips),
            isLand: false,
            isCardDraw: true,
            cardsDrawn,
            isRepeatedDraw: false,
        };
    }
    return null;
}
// ─── Public: classifySpells ───────────────────────────────────────────────────
/**
 * Convert non-commander, non-land deck entries to SimSpellCard[].
 * Mana producers and card draw effects are included as subtypes.
 */
export function classifySpells(entries) {
    const result = [];
    for (const entry of entries) {
        // Skip commanders
        if (entry.isCommander)
            continue;
        const { card } = entry;
        const typeLine = card.typeLine ?? '';
        // Skip lands (including true lands and modal DFCs with a land face;
        // conditional-transform cards like Ojer Axonil are NOT lands)
        if (isLand(card))
            continue;
        const manaCost = card.manaCost ?? card.cardFaces?.[0]?.manaCost ?? '';
        const oracleText = card.oracleText ?? card.cardFaces?.map((f) => f.oracleText ?? '').join('\n') ?? '';
        const { requirements, cmc } = parseManaRequirements(manaCost);
        const hasColoredPips = Array.from(requirements.keys()).some((k) => /^[WUBRG]$/.test(k) || /^[WUBRG]\/[WUBRG]$/.test(k));
        const weight = computeSpellWeight(cmc, hasColoredPips);
        // Skip colorless spells (weight = 0, no pips to track)
        if (weight === 0 && !hasColoredPips) {
            // Still include mana producers even if colorless
            const producer = classifyManaProducer(card.name, cmc, typeLine, oracleText, requirements, false);
            if (producer) {
                result.push(producer);
                continue;
            }
            continue;
        }
        // Check for mana producer (highest priority)
        const producer = classifyManaProducer(card.name, cmc, typeLine, oracleText, requirements, hasColoredPips);
        if (producer) {
            result.push(producer);
            continue;
        }
        // Check for card draw
        const draw = classifyCardDraw(card.name, cmc, oracleText, requirements, hasColoredPips);
        if (draw) {
            result.push(draw);
            continue;
        }
        // Plain spell — include only if it has colored requirements
        if (weight > 0) {
            result.push({
                name: card.name,
                cmc,
                colorRequirements: requirements,
                weight,
                isLand: false,
            });
        }
    }
    return result;
}
// ─── Public: classifyCommanders ───────────────────────────────────────────────
/**
 * Convert commander deck entries to SimCommander[].
 */
export function classifyCommanders(entries) {
    return entries
        .filter((e) => e.isCommander)
        .map((entry) => {
        const { card } = entry;
        const manaCost = card.manaCost ?? card.cardFaces?.[0]?.manaCost ?? '';
        const oracleText = card.oracleText ?? card.cardFaces?.map((f) => f.oracleText ?? '').join('\n') ?? '';
        const { requirements, cmc } = parseManaRequirements(manaCost);
        const colorIdentity = new Set(card.colorIdentity);
        // Check for mana production
        const typeLine = card.typeLine ?? '';
        let producesMana = false;
        let manaProducedColors;
        if (/\{t\}: add/i.test(oracleText) || /add .{0,10}mana/i.test(oracleText)) {
            producesMana = true;
            manaProducedColors = parseProducedColors(oracleText);
            if (manaProducedColors.size === 0) {
                // Default to commander's color identity
                manaProducedColors = new Set(colorIdentity);
            }
        }
        // Check for card draw
        let drawsCards = false;
        let cardsPerTurn;
        if (/at the beginning of (your|each) upkeep.*draw|whenever .+ draw|each (turn|upkeep).*(draw|draws)/i.test(oracleText)) {
            drawsCards = true;
            cardsPerTurn = 1;
        }
        return {
            name: card.name,
            cmc,
            colorRequirements: requirements,
            colorIdentity,
            weight: 3.0,
            ...(producesMana ? { producesMana: true } : {}),
            ...(manaProducedColors !== undefined ? { manaProducedColors } : {}),
            ...(drawsCards ? { drawsCards: true } : {}),
            ...(cardsPerTurn !== undefined ? { cardsPerTurn } : {}),
        };
    });
}
//# sourceMappingURL=sim-classifier.js.map